// src/components/EventActionBindingModal.jsx
import { useState, useEffect, useMemo } from 'react';
import { ModalForm } from './Modal';
import { useJobSchemas } from '../hooks/useJobSchemas';
import { useEventTypeSchemas } from '../hooks/useEventTypeSchemas';
import { DocumentTextIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const JOB_PRIORITY_LABELS = {
    0: 'LOW',
    1: 'NORMAL',
    2: 'HIGH',
};

// Job types that use workflow execution with {templateName, input: {...}} structure
const WORKFLOW_JOB_TYPES = ['run_workflow', 'run_company_workflows'];

// Params resolved from event payload via $.reference — companyId/documentId go in param mapping,
// but tenderId is extracted separately by the dispatcher via the binding's tenderIdField.
const PAYLOAD_REF_FIELDS = ['companyId', 'documentId'];

/**
 * Build an event-action param mapping template for a job type.
 *
 * The dispatcher's mapParameters() resolves "$.fieldName" against the event payload.
 * The result becomes job.Parameters (JSONB). tenderId is handled separately by the
 * dispatcher via extractTenderID + binding.tenderIdField and should NOT be in the mapping.
 *
 * For workflow jobs: { "templateName": "", "input": { "companyId": "$.companyId", ... } }
 * For regular jobs:  { "companyId": "$.companyId", "force": false, ... }
 */
const buildEventActionTemplate = (jobTypeSchema) => {
    if (!jobTypeSchema?.params?.length) return '{}';

    const isWorkflow = WORKFLOW_JOB_TYPES.includes(jobTypeSchema.key);

    // For workflow jobs, params inside "input" are the workflow template inputs,
    // while templateName/staggerSeconds/companyIds are top-level job params.
    // The schema lists ALL params flat, but we need to separate them.
    const WORKFLOW_TOP_LEVEL = ['templateName', 'staggerSeconds', 'companyIds'];

    const inputParams = {};
    const topLevel = {};

    jobTypeSchema.params.forEach(param => {
        // Skip tenderId — handled by binding's tenderIdField, not param mapping
        if (param.jsonName === 'tenderId') return;

        // Use $.reference for fields resolved from event payload
        if (PAYLOAD_REF_FIELDS.includes(param.jsonName)) {
            const target = isWorkflow && !WORKFLOW_TOP_LEVEL.includes(param.jsonName) ? inputParams : topLevel;
            target[param.jsonName] = `$.${param.jsonName}`;
            return;
        }

        // Parse default value or use sensible placeholder
        let value = param.default;
        if (value === null || value === undefined || value === '') {
            const type = param.type?.replace(/^\*/, '') || 'string';
            if (['bool'].includes(type)) value = false;
            else if (['int', 'int32', 'int64', 'float32', 'float64'].includes(type)) value = 0;
            else if (type.startsWith('[]')) value = [];
            else value = '';
        } else {
            const type = param.type?.replace(/^\*/, '') || 'string';
            if (['bool'].includes(type)) value = value === 'true';
            else if (['int', 'int32', 'int64', 'float32', 'float64'].includes(type)) {
                const num = parseFloat(value);
                value = isNaN(num) ? 0 : num;
            }
        }

        if (isWorkflow && !WORKFLOW_TOP_LEVEL.includes(param.jsonName)) {
            inputParams[param.jsonName] = value;
        } else {
            topLevel[param.jsonName] = value;
        }
    });

    if (isWorkflow) {
        return JSON.stringify({ ...topLevel, input: inputParams }, null, 2);
    }

    return JSON.stringify(topLevel, null, 2);
};

const EMPTY_FORM = {
    eventType: '',
    jobType: '',
    description: '',
    condition: '',
    jobPriority: 1,
    tenderIdField: 'tenderId',
    companyId: '',
    paramMapping: '',
};

const EventActionBindingModal = ({ isOpen, onClose, onSaved, binding, executeMutation, mutations }) => {
    const isEdit = !!binding;
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const {
        jobTypes,
        jobTypesByCategory,
        categories,
        getJobType,
        loading: schemasLoading,
    } = useJobSchemas();

    const {
        eventTypesByCategory,
        categories: eventCategories,
        getEventType,
        loading: eventSchemasLoading,
    } = useEventTypeSchemas();

    // Resolve the selected job type schema for description + param hints
    const selectedJobSchema = useMemo(() => {
        if (!form.jobType) return null;
        return getJobType(form.jobType);
    }, [form.jobType, getJobType]);

    // Resolve the selected event type schema for description + payload hints
    const selectedEventSchema = useMemo(() => {
        if (!form.eventType) return null;
        return getEventType(form.eventType);
    }, [form.eventType, getEventType]);

    // Params relevant for param mapping — exclude tenderId (handled by binding's tenderIdField)
    const mappableParams = useMemo(() => {
        if (!selectedJobSchema?.params) return [];
        return selectedJobSchema.params.filter(p => p.jsonName !== 'tenderId');
    }, [selectedJobSchema]);

    useEffect(() => {
        if (isOpen) {
            if (binding) {
                setForm({
                    eventType: binding.eventType || '',
                    jobType: binding.jobType || '',
                    description: binding.description || '',
                    condition: binding.condition || '',
                    jobPriority: binding.jobPriority ?? 1,
                    tenderIdField: binding.tenderIdField || 'tenderId',
                    companyId: binding.companyId || '',
                    paramMapping: binding.paramMapping ? JSON.stringify(binding.paramMapping, null, 2) : '',
                });
            } else {
                setForm(EMPTY_FORM);
            }
            setError('');
        }
    }, [isOpen, binding]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleJobTypeChange = (jobTypeKey) => {
        handleChange('jobType', jobTypeKey);
        // Auto-fill description from schema if empty and creating new
        if (!isEdit && !form.description) {
            const schema = getJobType(jobTypeKey);
            if (schema?.description) {
                handleChange('description', schema.description.split('.')[0]);
            }
        }
    };

    const handleFillTemplate = () => {
        if (!selectedJobSchema) return;
        const template = buildEventActionTemplate(selectedJobSchema);
        handleChange('paramMapping', template);
    };

    const handleSubmit = async () => {
        setError('');

        if (!form.eventType.trim()) {
            setError('Event type is required');
            return;
        }
        if (!form.jobType.trim()) {
            setError('Job type is required');
            return;
        }

        let parsedParamMapping = null;
        if (form.paramMapping.trim()) {
            try {
                parsedParamMapping = JSON.parse(form.paramMapping);
            } catch {
                setError('Param mapping must be valid JSON');
                return;
            }
        }

        const input = {
            eventType: form.eventType.trim(),
            jobType: form.jobType.trim(),
            jobPriority: Number(form.jobPriority),
        };

        if (form.description.trim()) input.description = form.description.trim();
        if (form.condition.trim()) input.condition = form.condition.trim();
        if (form.tenderIdField.trim() && form.tenderIdField.trim() !== 'tenderId') {
            input.tenderIdField = form.tenderIdField.trim();
        }
        if (form.companyId.trim()) input.companyId = form.companyId.trim();
        if (parsedParamMapping) input.paramMapping = parsedParamMapping;

        setSaving(true);
        try {
            let result;
            if (isEdit) {
                result = await executeMutation(mutations.adminUpdateEventActionBinding, {
                    id: binding.id,
                    input,
                });
                const data = result.adminUpdateEventActionBinding;
                if (!data?.success) {
                    setError(data?.error || 'Failed to update binding');
                    return;
                }
            } else {
                result = await executeMutation(mutations.adminCreateEventActionBinding, { input });
                const data = result.adminCreateEventActionBinding;
                if (!data?.success) {
                    setError(data?.error || 'Failed to create binding');
                    return;
                }
            }
            onSaved?.();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const inputClass = 'w-full bg-gray-800 border border-gray-700  px-3 py-2 text-white text-sm font-mono focus:border-gray-500 focus:outline-none';
    const labelClass = 'block text-xs font-mono font-bold text-gray-400 mb-1 uppercase';

    return (
        <ModalForm
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={handleSubmit}
            title={isEdit ? 'Edit Binding' : 'Create Binding'}
            submitLabel={isEdit ? 'Update' : 'Create'}
            isLoading={saving}
            size="xl"
        >
            {error && (
                <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700 font-mono">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Event Type *</label>
                    {eventSchemasLoading ? (
                        <div className={`${inputClass} text-gray-500`}>Loading event types...</div>
                    ) : (
                        <select
                            value={form.eventType}
                            onChange={(e) => handleChange('eventType', e.target.value)}
                            className={inputClass}
                        >
                            <option value="">Select event type...</option>
                            {eventCategories.map(cat => {
                                const catEvents = eventTypesByCategory[cat.id] || [];
                                if (catEvents.length === 0) return null;
                                return (
                                    <optgroup key={cat.id} label={cat.label}>
                                        {catEvents.map(et => (
                                            <option key={et.eventType} value={et.eventType}>{et.eventType}</option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </select>
                    )}
                    {selectedEventSchema?.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{selectedEventSchema.description}</p>
                    )}
                </div>

                <div>
                    <label className={labelClass}>Job Type *</label>
                    {schemasLoading ? (
                        <div className={`${inputClass} text-gray-500`}>Loading job types...</div>
                    ) : (
                        <select
                            value={form.jobType}
                            onChange={(e) => handleJobTypeChange(e.target.value)}
                            className={inputClass}
                        >
                            <option value="">Select job type...</option>
                            {Object.values(categories).map(cat => {
                                const catJobs = jobTypesByCategory[cat.id] || [];
                                if (catJobs.length === 0) return null;
                                return (
                                    <optgroup key={cat.id} label={cat.label}>
                                        {catJobs.map(jt => (
                                            <option key={jt.key} value={jt.key}>{jt.label}</option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                            {/* Include job types not in any known category */}
                            {jobTypes.filter(jt => !jt.category || !categories[jt.category]).length > 0 && (
                                <optgroup label="Other">
                                    {jobTypes.filter(jt => !jt.category || !categories[jt.category]).map(jt => (
                                        <option key={jt.key} value={jt.key}>{jt.label}</option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    )}
                    {selectedJobSchema?.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{selectedJobSchema.description}</p>
                    )}
                </div>
            </div>

            <div>
                <label className={labelClass}>Description</label>
                <input
                    type="text"
                    value={form.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Human-readable description of this binding"
                    className={inputClass}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Condition</label>
                    <input
                        type="text"
                        value={form.condition}
                        onChange={(e) => handleChange('condition', e.target.value)}
                        placeholder={selectedEventSchema?.payloadFieldNames?.length
                            ? `e.g. ${selectedEventSchema.payloadFieldNames[0]}==VALUE`
                            : 'e.g. toState==ASSESSED'}
                        className={inputClass}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Expression evaluated against event payload
                        {selectedEventSchema?.payloadFieldNames?.length > 0 && (
                            <> (fields: {selectedEventSchema.payloadFieldNames.join(', ')})</>
                        )}
                    </p>
                </div>

                <div>
                    <label className={labelClass}>Job Priority</label>
                    <select
                        value={form.jobPriority}
                        onChange={(e) => handleChange('jobPriority', Number(e.target.value))}
                        className={inputClass}
                    >
                        {Object.entries(JOB_PRIORITY_LABELS).map(([val, label]) => (
                            <option key={val} value={val}>{label} ({val})</option>
                        ))}
                    </select>
                </div>
            </div>

            {selectedEventSchema?.payload?.length > 0 && (
                <div className="bg-gray-900 border border-gray-700 p-2">
                    <p className="text-xs font-mono text-gray-500 mb-1.5 uppercase font-bold">Event Payload Fields</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        {selectedEventSchema.payload.map(p => (
                            <div key={p.name} className="flex items-center gap-1.5 text-xs font-mono">
                                <span className="text-gray-300">{p.name}</span>
                                <span className="text-gray-600">{p.type}</span>
                                {p.description && <span className="text-gray-600 truncate" title={p.description}>- {p.description}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Tender ID Field</label>
                    <input
                        type="text"
                        value={form.tenderIdField}
                        onChange={(e) => handleChange('tenderIdField', e.target.value)}
                        placeholder="tenderId"
                        className={inputClass}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Field name in event payload containing the tender ID
                        {selectedEventSchema?.payloadFieldNames?.length > 0 && (
                            <> (payload fields: {selectedEventSchema.payloadFieldNames.join(', ')})</>
                        )}
                    </p>
                </div>

                <div>
                    <label className={labelClass}>Company ID</label>
                    <input
                        type="text"
                        value={form.companyId}
                        onChange={(e) => handleChange('companyId', e.target.value)}
                        placeholder="Optional: scope to company"
                        className={inputClass}
                    />
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-1">
                    <label className={labelClass + ' mb-0'}>Param Mapping (JSON)</label>
                    {selectedJobSchema && (
                        <button
                            type="button"
                            onClick={handleFillTemplate}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-mono font-bold text-primary hover:text-white bg-gray-800 border border-gray-700 hover:border-gray-600 uppercase"
                        >
                            <DocumentTextIcon className="h-3.5 w-3.5" />
                            Fill Template
                        </button>
                    )}
                </div>
                <textarea
                    value={form.paramMapping}
                    onChange={(e) => handleChange('paramMapping', e.target.value)}
                    placeholder={selectedJobSchema
                        ? WORKFLOW_JOB_TYPES.includes(form.jobType)
                            ? '{"templateName": "customer-onboarding", "input": {"companyId": "$.companyId", "minScore": 40}}'
                            : '{"companyId": "$.companyId", "force": true}'
                        : 'Select a job type to see available parameters'}
                    rows={5}
                    className={`${inputClass} resize-y`}
                />
                {/* $.reference syntax hint */}
                <div className="mt-1.5 flex items-start gap-1.5 text-xs text-gray-500">
                    <InformationCircleIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                        Use <code className="text-gray-400">$.fieldName</code> to resolve values from the event payload at dispatch time (e.g. <code className="text-gray-400">$.companyId</code>). Literal values pass through as-is. <code className="text-gray-400">tenderId</code> is extracted separately via Tender ID Field above.
                        {WORKFLOW_JOB_TYPES.includes(form.jobType) && (
                            <> Workflow jobs need <code className="text-gray-400">templateName</code> at top level and workflow inputs inside <code className="text-gray-400">input</code>.</>
                        )}
                    </span>
                </div>
                {/* Parameter hints for selected job type */}
                {mappableParams.length > 0 && (
                    <div className="mt-2 bg-gray-900 border border-gray-700 p-2">
                        <p className="text-xs font-mono text-gray-500 mb-1.5 uppercase font-bold">Available Parameters</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                            {mappableParams.map(p => (
                                <div key={p.jsonName} className="flex items-center gap-1.5 text-xs font-mono">
                                    <span className="text-gray-300">{p.jsonName}</span>
                                    <span className="text-gray-600">{p.type}</span>
                                    {p.required && <span className="text-red-700">*</span>}
                                    {p.default && <span className="text-gray-600">={p.default}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ModalForm>
    );
};

export default EventActionBindingModal;
